/**
 * A validade do acesso da organização e a marca d'água, do lado da tela (`A-09`, ADR-060).
 *
 * As contas usam o **relógio do navegador**, e não o da demonstração (`agoraDoInquilino`): a
 * validade é comercial, como a fatura, e a hora da marca d'água diz quando o print foi tirado
 * — os dois são do mundo, não do tempo avançado de uma apresentação.
 */

/** Quantos dias antes do fim o administrador vê o aviso (o mesmo número da API). */
export const DIAS_DE_AVISO = 7;

/**
 * O instante em que o acesso termina: a meia-noite que fecha o dia `acesso_ate` em Brasília.
 * O Brasil não tem horário de verão desde 2019: o fuso é -03:00 o ano inteiro.
 */
export function fimDoAcesso(acessoAte: string | null | undefined): Date | null {
  if (!acessoAte) return null;
  const fim = new Date(`${acessoAte}T00:00:00-03:00`);
  fim.setUTCDate(fim.getUTCDate() + 1);
  return fim;
}

/** `30/11/2026`, sem passar por fuso: a data é a de Brasília, como veio. */
export function dataDoAcesso(acessoAte: string): string {
  const [ano, mes, dia] = acessoAte.split("-");
  return `${dia}/${mes}/${ano}`;
}

/**
 * Quantos dias de acesso faltam, contando o de hoje: no último dia, 1. Nulo sem prazo ou com
 * o acesso já vencido.
 */
export function diasDeAcesso(
  acessoAte: string | null | undefined,
  agora: Date = new Date(),
): number | null {
  const fim = fimDoAcesso(acessoAte);
  if (!fim || agora >= fim) return null;
  return Math.ceil((fim.getTime() - agora.getTime()) / 86_400_000);
}

/**
 * Se o aviso de que o acesso está acabando aparece: só para o administrador, só nos últimos
 * `DIAS_DE_AVISO` dias (escolha de 09/10).
 */
export function avisoDeValidadeVisivel(
  acessoAte: string | null | undefined,
  perfil: string | undefined,
  agora?: Date,
): boolean {
  if (perfil !== "ADMIN" || !acessoAte) return false;
  const dias = diasDeAcesso(acessoAte, agora);
  return dias !== null && dias <= DIAS_DE_AVISO;
}

/** A frase da marca d'água: quem está vendo e quando. */
export function textoDaMarca(email: string, agora: Date = new Date()): string {
  const quando = agora.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
  return `${email} · ${quando}`;
}

function escaparXml(texto: string): string {
  return texto
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const GRAUS_22 = (22 * Math.PI) / 180;

/** O ladrilho da marca d'água, pronto para `background-image`, e o tamanho dele. */
export interface Ladrilho {
  imagem: string;
  largura: number;
  altura: number;
}

/**
 * O ladrilho da marca d'água: a frase uma vez, em diagonal, no centro de um SVG do tamanho
 * dela inclinada — e-mail comprido não sai cortado na borda, e as frases das fileiras vizinhas
 * não se encostam. O navegador repete o ladrilho pela tela inteira. Cinza médio e bem leve:
 * aparece sobre o escuro e sobre o claro, e não atrapalha a leitura de placa nenhuma. A cor
 * não é token de propósito: é imagem, e a mesma nos dois temas — um SVG em `data:` nem
 * enxerga as variáveis do CSS.
 */
export function ladrilhoDaMarca(texto: string): Ladrilho {
  // ~7,2 px por caractere a 13 px, em negrito; a inclinação é de 22°.
  const comprimento = texto.length * 7.2;
  const largura = Math.ceil(comprimento * Math.cos(GRAUS_22) + 90);
  const altura = Math.ceil(comprimento * Math.sin(GRAUS_22) + 70);
  const svg =
    `<svg xmlns="http://www.w3.org/2000/svg" width="${largura}" height="${altura}">` +
    `<text x="${largura / 2}" y="${altura / 2}" text-anchor="middle" ` +
    `dominant-baseline="middle" transform="rotate(-22 ${largura / 2} ${altura / 2})" ` +
    `fill="gray" fill-opacity="0.15" font-family="system-ui, sans-serif" ` +
    `font-size="13" font-weight="600">${escaparXml(texto)}</text></svg>`;
  // Parênteses e aspas simples também codificados: `encodeURIComponent` os deixa, e dentro de
  // `url(...)` um parêntese solto encerra o endereço para mais de um leitor de CSS.
  const codificado = encodeURIComponent(svg).replace(
    /[()']/g,
    (c) => `%${c.charCodeAt(0).toString(16).toUpperCase()}`,
  );
  return {
    imagem: `url("data:image/svg+xml;charset=utf-8,${codificado}")`,
    largura,
    altura,
  };
}
